class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this.fuzzyResultsLimit = parseInt(this.dataset.resultsLimit, 10) || 6;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
    PredictiveSearch.loadProductIndex();
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
    this.augmentWithFuzzyMatches();
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
        this.augmentWithFuzzyMatches();
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  async augmentWithFuzzyMatches() {
    const query = this.getQuery();
    if (query.length < 2) return;

    const typeFilter = this.searchProductTypes && this.searchProductTypes.value
      ? this.searchProductTypes.value.toLowerCase()
      : null;

    let products;
    try {
      products = await PredictiveSearch.loadProductIndex();
    } catch (e) {
      return;
    }
    if (!products || products.length === 0) return;
    if (this.getQuery() !== query) return;

    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const resultsRoot = document.getElementById(id);
    if (!resultsRoot) return;

    const existingHandles = new Set(
      Array.from(resultsRoot.querySelectorAll('a[href*="/products/"]'))
        .map((a) => {
          const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
          return m ? m[1] : null;
        })
        .filter(Boolean)
    );

    const matches = PredictiveSearch.fuzzyMatch(query, products, typeFilter)
      .filter((p) => !existingHandles.has(p.handle))
      .slice(0, this.fuzzyResultsLimit);

    if (matches.length === 0) return;

    let grid = resultsRoot.querySelector('#predictive-search-results-products-list');
    if (grid) {
      const fragment = document.createDocumentFragment();
      matches.forEach((p) => fragment.appendChild(PredictiveSearch.buildProductCard(p)));
      grid.appendChild(fragment);
    } else {
      const emptyState = resultsRoot.querySelector('.empty-state');
      if (emptyState) emptyState.remove();

      const wrap = document.createElement('div');
      wrap.className = 'flex w-full flex-col gap-y-6 md:flex-row';
      wrap.innerHTML = `
        <div class="flex flex-col flex-grow search__box-products order-last md:order-first">
          <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
            <div class="swipe-mobile swipe-mobile--2-cols">
              <div id="predictive-search-results-products-list" class="f-grid swipe-mobile__inner"></div>
            </div>
          </div>
        </div>`;
      resultsRoot.appendChild(wrap);
      grid = wrap.querySelector('#predictive-search-results-products-list');
      matches.forEach((p) => grid.appendChild(PredictiveSearch.buildProductCard(p)));
    }

    this.setAttribute('results', '');
  }

  static loadProductIndex() {
    if (PredictiveSearch._productsPromise) return PredictiveSearch._productsPromise;
    PredictiveSearch._productsPromise = (async () => {
      const all = [];
      const base = (window.shopUrl || '') + (FoxTheme?.routes?.root_url || '/');
      const root = base.endsWith('/') ? base : base + '/';
      for (let page = 1; page <= 4; page++) {
        try {
          const res = await fetch(`${root}products.json?limit=250&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          if (!data?.products?.length) break;
          all.push(...data.products);
          if (data.products.length < 250) break;
        } catch (e) {
          break;
        }
      }
      return all;
    })();
    return PredictiveSearch._productsPromise;
  }

  static fuzzyMatch(query, products, typeFilter) {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const qTokens = q.split(/\s+/).filter(Boolean);
    const scored = [];

    for (const product of products) {
      if (typeFilter && (product.product_type || '').toLowerCase() !== typeFilter) continue;

      const title = (product.title || '').toLowerCase();
      const haystack = [
        title,
        product.product_type,
        product.vendor,
        Array.isArray(product.tags) ? product.tags.join(' ') : product.tags,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      let score = Infinity;

      let allTokensSubstring = true;
      let bestSubstringScore = 0;
      for (const tok of qTokens) {
        const idx = haystack.indexOf(tok);
        if (idx === -1) {
          allTokensSubstring = false;
          break;
        }
        bestSubstringScore += idx === 0 ? 0 : (title.indexOf(tok) === 0 ? 1 : 2);
      }
      if (allTokensSubstring) {
        score = bestSubstringScore;
      } else {
        const maxDist = q.length <= 4 ? 1 : q.length <= 7 ? 2 : 3;
        const words = haystack.split(/\s+/).filter(Boolean);
        let best = Infinity;
        for (const word of words) {
          if (Math.abs(word.length - q.length) <= maxDist) {
            const d = PredictiveSearch.levenshtein(q, word);
            if (d < best) best = d;
          }
          if (word.length > q.length) {
            const limit = Math.min(word.length - q.length, 6);
            for (let i = 0; i <= limit; i++) {
              const sub = word.substr(i, q.length);
              const d = PredictiveSearch.levenshtein(q, sub);
              if (d < best) best = d;
              if (best === 0) break;
            }
          }
          if (best === 0) break;
        }
        if (best <= maxDist) score = 100 + best;
      }

      if (score !== Infinity) scored.push({ product, score });
    }

    scored.sort((a, b) => a.score - b.score);
    return scored.map((s) => s.product);
  }

  static levenshtein(a, b) {
    if (a === b) return 0;
    const al = a.length;
    const bl = b.length;
    if (al === 0) return bl;
    if (bl === 0) return al;
    let prev = new Array(bl + 1);
    let curr = new Array(bl + 1);
    for (let j = 0; j <= bl; j++) prev[j] = j;
    for (let i = 1; i <= al; i++) {
      curr[0] = i;
      for (let j = 1; j <= bl; j++) {
        const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      }
      [prev, curr] = [curr, prev];
    }
    return prev[bl];
  }

  static buildProductCard(product) {
    const url = `${FoxTheme?.routes?.root_url || '/'}products/${product.handle}`.replace('//', '/');
    const img = product.images && product.images[0];
    const imgSrc = img ? (typeof img === 'string' ? img : img.src) : null;
    const variant = product.variants && product.variants[0];
    const priceCents = variant ? Math.round(parseFloat(variant.price) * 100) : null;
    const compareCents = variant && variant.compare_at_price
      ? Math.round(parseFloat(variant.compare_at_price) * 100)
      : null;
    const onSale = compareCents && compareCents > priceCents;

    const fmt = (cents) =>
      FoxTheme?.utils?.formatMoney
        ? FoxTheme.utils.formatMoney(cents, FoxTheme.settings?.moneyFormat)
        : (cents / 100).toFixed(2);

    const card = document.createElement('div');
    card.className = 'product-card';
    card.innerHTML = `
      <div class="product-card__wrapper h-full">
        <div class="product-card__image-wrapper product-card__image-wrapper--main-only">
          <a href="${url}" aria-label="${PredictiveSearch.escapeHtml(product.title)}">
            <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio:1;">
              ${imgSrc ? `<img src="${imgSrc}" alt="${PredictiveSearch.escapeHtml(product.title)}" loading="lazy">` : ''}
            </div>
          </a>
        </div>
        <div class="product-card__info">
          <a href="${url}" class="product-card__title reversed-link">${PredictiveSearch.escapeHtml(product.title)}</a>
          ${priceCents !== null ? `
            <div class="product-card__price price${onSale ? ' price--on-sale' : ''}">
              <span class="price__regular">${fmt(priceCents)}</span>
              ${onSale ? `<s class="price__sale text-subtext">${fmt(compareCents)}</s>` : ''}
            </div>` : ''}
        </div>
      </div>`;
    return card;
  }

  static escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
